import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Airport Buddy</title>
        <meta name="description" content="Airport Buddy's privacy policy. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://airportbuddy.app/privacy" />
        <meta property="og:title" content="Privacy Policy - Airport Buddy" />
        <meta property="og:description" content="How Airport Buddy collects, uses, and protects your personal information." />
        <meta property="og:url" content="https://airportbuddy.app/privacy" />
        <meta name="twitter:title" content="Privacy Policy - Airport Buddy" />
        <meta name="twitter:description" content="How Airport Buddy collects, uses, and protects your personal information." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="text-center space-y-4 mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Privacy Policy
              </h1>
              <p className="text-muted-foreground">
                Last updated: March 31, 2026
              </p>
            </header>

            <Card>
              <CardContent className="pt-6 space-y-6 prose prose-sm max-w-none">
                <section>
                  <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
                  <p className="text-muted-foreground">
                    Airport Buddy ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
                    This privacy policy explains how we collect, use, and safeguard your information when you use our website, 
                    progressive web app (PWA), and native iOS application (collectively, the "Service").
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>

                  <h3 className="text-lg font-semibold mb-2">2.1 Account Information</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                    <li>Email address and password when you create an account</li>
                    <li>Display name, if provided</li>
                    <li>Subscription and payment status (plan tier, renewal dates)</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-2">2.2 Email Collection</h3>
                  <p className="text-muted-foreground mb-2">
                    We collect email addresses through newsletter signup forms, exit-intent popups, and airport-specific sign-up prompts. 
                    These emails are stored in our database alongside the capture source (e.g., "homepage" or "airport_LAX") so we can 
                    send relevant travel alerts and product updates. You may unsubscribe at any time using the link in any email.
                  </p>

                  <h3 className="text-lg font-semibold mb-2">2.3 Location Data</h3>
                  <p className="text-muted-foreground mb-2">
                    With your explicit permission, we access your device's GPS coordinates to detect the nearest airport 
                    and display relevant wait times and travel information. Location data is processed on-device using a 
                    distance algorithm and is <strong>not</strong> stored on our servers or shared with third parties. 
                    You can revoke location access at any time through your device settings.
                  </p>

                  <h3 className="text-lg font-semibold mb-2">2.4 Push Notification Tokens</h3>
                  <p className="text-muted-foreground mb-2">
                    If you opt in to push notifications, we store your device push token (e.g., APNs token on iOS) in our 
                    database, linked to your user account. This token is used solely to deliver wait-time alerts, flight 
                    status updates, and gate change notifications that you have configured. We store the token alongside 
                    your platform type (iOS/web) and update it when your device token changes. You can disable push 
                    notifications at any time in the app's Notification settings or through your device settings, which 
                    removes your token from our systems.
                  </p>

                  <h3 className="text-lg font-semibold mb-2">2.5 AI Chat Messages</h3>
                  <p className="text-muted-foreground mb-2">
                    Our AI travel assistant allows you to ask airport-related questions. Messages you send are transmitted 
                    to a third-party AI model (Google Gemini) for processing and response generation. We do <strong>not</strong> store 
                    your chat messages on our servers — they exist only in your device's local session memory for the duration 
                    of the conversation. Chat history is cleared when you close the chat window. Free users are limited to 
                    5 messages per day, tracked via local device storage only. We do not use your chat content for training 
                    AI models.
                  </p>

                  <h3 className="text-lg font-semibold mb-2">2.6 Flight &amp; Airport Selections</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                    <li>Airports you save to your favorites (linked to your account)</li>
                    <li>Flight information you choose to track</li>
                    <li>Terminal and checkpoint preferences</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-2">2.7 Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Device information (browser type, operating system, app version)</li>
                    <li>Usage data (pages visited, features used)</li>
                    <li>IP address and general geographic region</li>
                    <li>Cookies and similar tracking technologies (web only)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">3. How We Use Your Information</h2>
                  <p className="text-muted-foreground mb-2">We use your information to:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Provide real-time airport wait times, flight status, and gate change alerts</li>
                    <li>Detect your nearest airport and show relevant information</li>
                    <li>Deliver push notifications you have opted into</li>
                    <li>Process AI chat queries and return helpful travel answers</li>
                    <li>Send travel alert emails to subscribers</li>
                    <li>Manage your subscription and payment status</li>
                    <li>Maintain, improve, and secure our service</li>
                    <li>Respond to your inquiries and support requests</li>
                    <li>Analyze usage patterns to enhance user experience</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">4. Data Sharing and Disclosure</h2>
                  <p className="text-muted-foreground mb-2">
                    We do not sell your personal information. We may share your data only in these limited circumstances:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li><strong>AI Processing:</strong> Chat messages are sent to Google's Gemini API for response generation; Google's privacy policy applies to that processing</li>
                    <li><strong>Payment Processors:</strong> Stripe (web) and Apple (iOS) process payments; we do not store your payment card details</li>
                    <li><strong>Email Delivery:</strong> We use Resend to send transactional and marketing emails</li>
                    <li><strong>Infrastructure:</strong> Our backend is hosted on secure cloud infrastructure with encryption at rest and in transit</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">5. Data Security</h2>
                  <p className="text-muted-foreground">
                    We implement appropriate technical and organizational measures to protect your personal information, 
                    including encryption in transit (TLS), row-level security on database tables, and secure authentication. 
                    However, no internet transmission is completely secure, and we cannot guarantee absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">6. Data Retention</h2>
                  <p className="text-muted-foreground">
                    We retain your account data for as long as your account is active. Email subscriber data is retained 
                    until you unsubscribe. Push tokens are removed when you disable notifications or delete your account. 
                    AI chat messages are not retained. Flight cache data is refreshed periodically and older entries are 
                    automatically purged.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">7. Your Rights &amp; Account Deletion</h2>
                  <p className="text-muted-foreground mb-2">You have the right to:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Access your personal data</li>
                    <li>Correct inaccurate information</li>
                    <li>Request deletion of your data</li>
                    <li>Object to processing of your data</li>
                    <li>Export your data</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                  <p className="text-muted-foreground mt-2">
                    You can delete your account directly from the Settings page in our iOS app or by contacting us. 
                    Account deletion removes your saved airports, notification preferences, push tokens, and subscription data.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">8. Cookies</h2>
                  <p className="text-muted-foreground">
                    We use cookies and similar technologies on our web version to enhance your experience, analyze usage, 
                    and provide personalized content. The iOS app does not use cookies. You can control cookies through 
                    your browser settings, though some web features may not function properly if cookies are disabled.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">9. Children's Privacy</h2>
                  <p className="text-muted-foreground">
                    Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13. 
                    If you believe we have collected such information, please contact us immediately.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">10. Changes to This Policy</h2>
                  <p className="text-muted-foreground">
                    We may update this privacy policy from time to time. We will notify you of significant changes by posting the new 
                    policy on this page and updating the "Last updated" date.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">11. Contact Us</h2>
                  <p className="text-muted-foreground">
                    If you have questions about this privacy policy or our data practices, please contact us at:
                  </p>
                  <p className="text-muted-foreground mt-2">
                    Email: <a href="mailto:privacy@airportbuddy.app" className="text-primary hover:underline">privacy@airportbuddy.app</a>
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
